import { useCallback, useEffect, useRef, useState } from "react";
import { post } from "../services/services";

const identity = (record) => record;

export const withMasterReview = (record) => ({
  ...record,
  key: String(record.garbage_record_id ?? record.deck_log_id),
  masterReviewed: record.reviewed_by_master || false,
});

export default function usePaginatedLogList(url, dataKey, mapRecord = identity) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState({ current: 1, pageSize: 10, total: 0 });
  const pageRef = useRef(page);
  const scopeRef = useRef(null);
  const requestId = useRef(0);

  useEffect(() => () => { requestId.current += 1; }, []);

  const load = useCallback(async (params, requestedPage, requestedSize) => {
    const scopeChanged = JSON.stringify(params) !== JSON.stringify(scopeRef.current);
    const current = requestedPage ?? (scopeChanged ? 1 : pageRef.current.current);
    const pageSize = requestedSize ?? pageRef.current.pageSize;
    scopeRef.current = params;
    const id = ++requestId.current;
    pageRef.current = { current, pageSize, total: scopeChanged ? 0 : pageRef.current.total };
    setPage(pageRef.current);
    setData([]);
    setError("");
    setLoading(true);

    try {
      const form = new FormData();
      Object.entries(params).forEach(([key, value]) => form.append(key, value));
      form.append("page", current);
      form.append("per_page", pageSize);
      const response = await post(url, form);
      if (id !== requestId.current) return;
      if (response?.status !== true) {
        throw new Error(response?.message || "Unable to load records. Please retry.");
      }
      const metadata = response.pagination;
      if (!metadata || !Array.isArray(response[dataKey])) {
        throw new Error("Unable to load paginated records. Please retry.");
      }
      const nextPage = {
        current: Number(metadata.current_page),
        pageSize: Number(metadata.per_page),
        total: Number(metadata.total),
      };
      pageRef.current = nextPage;
      setPage(nextPage);
      setData(response[dataKey].map(mapRecord));
    } catch (failure) {
      if (id === requestId.current) setError(failure.message || "Unable to load records.");
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [url, dataKey, mapRecord]);

  const retry = () => scopeRef.current && load(scopeRef.current);
  const pagination = {
    ...page,
    disabled: loading,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total) => `${total} records`,
    onChange: (current, pageSize) => {
      if (scopeRef.current) {
        load(scopeRef.current, pageSize !== pageRef.current.pageSize ? 1 : current, pageSize);
      }
    },
  };

  return { data, setData, loading, error, retry, load, pagination };
}
